/**
 * Page-context registry for the conversational assistant.
 *
 * Pages register named provider functions describing what they currently show; the chat
 * widget pulls a snapshot at send time, so whatever the page shows at that moment is what
 * goes out — no event bookkeeping, no staleness. Providers read component state through JS
 * APIs (DataTables API, storage store, React state), never scraped markup text.
 *
 * Everything here is a HINT for the assistant's reference resolution. The backend
 * re-validates every entity reference against the user's tenant and drops what fails —
 * nothing sent from here grants access to anything.
 *
 * Legacy (non-webpack) pages register through `window.TAPageContext`; scripts that may run
 * before this module loads push `[name, providerFn]` onto `window.__taPageContextQueue`
 * instead — the queue is drained on init and replaced with a direct-register shim.
 */

const createRegistry = () => {
    const providers = new Map();

    /**
     * Register a provider. Its return object is shallow-merged into every snapshot;
     * returning null/undefined contributes nothing. Re-registering a name replaces it.
     */
    const register = (name, providerFn) => {
        if (typeof providerFn === 'function') {
            providers.set(name, providerFn);
        }
    };

    /** Remove a provider (e.g. when an overlay unmounts and its context stops applying). */
    const unregister = (name) => {
        providers.delete(name);
    };

    const baseline = () => ({
        route: window.location.pathname,
        title: document.title || undefined,
        locale: document.body?.getAttribute('lang') || document.documentElement.lang || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    /**
     * The envelope sent with a chat message: baseline fields plus every provider's fragment.
     * A throwing provider contributes nothing and never blocks the send.
     */
    const snapshot = () => {
        const context = baseline();
        providers.forEach((providerFn, name) => {
            try {
                Object.assign(context, providerFn() || {});
            } catch (error) {
                console.warn(`pageContext provider "${name}" failed`, error);
            }
        });
        return context;
    };

    return { register, unregister, snapshot };
};

// The pages load SEVERAL webpack runtimes (layout + page-section compilations), so this
// module body can execute more than once as independent copies. The registry on window is
// the single source of truth: every copy adopts the existing one, so a provider registered
// by one bundle is visible to the widget snapshotting from another.
const pageContext =
    (typeof window !== 'undefined' && window.TAPageContext) || createRegistry();

if (typeof window !== 'undefined') {
    window.TAPageContext = pageContext;
    const queued = window.__taPageContextQueue;
    if (Array.isArray(queued)) {
        queued.forEach(([name, providerFn]) => pageContext.register(name, providerFn));
    }
    window.__taPageContextQueue = { push: ([name, providerFn]) => pageContext.register(name, providerFn) };
}

export default pageContext;
